import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import apiClient from '../services/api';

/**
 * Custom hook to manage referral code throughout the application
 * 
 * This hook:
 * 1. Extracts ref and schid from URL parameters
 * 2. Stores ref code in backend cache (IP-based fallback)
 * 3. Provides helper function to navigate to cart while preserving ref/schid
 * 
 * Usage:
 *   const { refCode, schoolId, navigateWithRefCode } = useRefCodeManagement();
 *   navigate(navigateWithRefCode("/cart"));
 */
export const useRefCodeManagement = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [refCode, setRefCode] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState<number | null>(null);
  const [refCodeProcessed, setRefCodeProcessed] = useState(false);

  // STEP 1: Extract ref and schid from URL parameters
  useEffect(() => {
    const paramRefCode = searchParams.get('ref');
    const paramSchoolId = searchParams.get('schid');

    if (paramRefCode) {
      setRefCode(paramRefCode);
    }
    if (paramSchoolId) {
      setSchoolId(parseInt(paramSchoolId, 10));
    }
  }, [searchParams]);

  // STEP 2: Store ref code in backend cache (IP-based backup for conversion tracking)
  useEffect(() => {
    const handleRefCode = async () => {
      if (refCode && !refCodeProcessed) {
        try {
          await apiClient.storeRefCode(refCode);
          console.log('Ref code stored successfully:', refCode);
          setRefCodeProcessed(true);
        } catch (refError) {
          console.error('Failed to store ref code:', refError);
          // Don't block the main workflow if ref code storage fails
        }
      }
    };

    handleRefCode();
  }, [refCode, refCodeProcessed]);

  // STEP 3: Helper function to navigate to cart while preserving ref/schid parameters
  const navigateWithRefCode = (path: string): string => {
    const params = new URLSearchParams();
    params.append('autoOpen', 'true');
    if (refCode) params.append('ref', refCode);
    if (schoolId) params.append('schid', schoolId.toString());
    return `${path}?${params.toString()}`;
  };

  return {
    refCode,
    schoolId,
    navigateWithRefCode,
  };
};
